// ════════════════════════════════════════════════════════════════════════════
// M2.7 Madurez de Procesos — cliente API PEMM-Empresa (Hammer · 0743)
// Consume /api/v1/process/madurez/empresa/*. Capacidad de transformación de la
// ORGANIZACIÓN: 13 capacidades en 4 categorías × E1-E4, global = eslabón débil (MIN).
// UNA evaluación por organización (no por proceso). Espejo de api-pemm-proceso.
// ════════════════════════════════════════════════════════════════════════════
import { request, getToken, BASE as API_ROOT } from '../../../shared/services/api';
import type { CategoriaEmpresa, ENivel } from './descriptores-pemm-empresa';
import type { RoadmapPemm, TcoRoiPemm } from './api-pemm-proceso';

export type NivelEmpresa = ENivel;
export type ColorProceso = 'VERDE' | 'AMARILLO' | 'ROJO';
export type EstadoEvalEmpresa = 'BORRADOR' | 'EN_REVISION' | 'APROBADA' | 'OBSOLETA';
export type TipoEjercicioEmpresa =
  | 'DIAGNOSTICO_INICIAL'
  | 'EVALUACION_PERIODICA'
  | 'POST_TRANSFORMACION'
  | 'AUDITORIA_EXTERNA';

/** Cabecera de una evaluación PEMM-Empresa. */
export interface EvaluacionEmpresa {
  id: string;
  codigo: string | null;
  tipoEjercicio: TipoEjercicioEmpresa;
  fechaEvaluacion: string;
  nivelGlobal: NivelEmpresa | null;
  estado: EstadoEvalEmpresa;
  observaciones?: string | null;
  /** Respuestas del cuestionario (0749): { `${capacidadClave}_qN`: { valor 1-4, nota? } }. */
  respuestas?: Record<string, { valor: number; nota?: string }> | null;
}

export interface ZenicEmpresa {
  cualitativa?: string;
  cuantitativa?: string;
  analisis?: string;
  leccion?: string;
}

/** Una celda del heatmap PEMM-Empresa: el nivel asignado a una capacidad. */
export interface CapacidadEvaluacionEmp {
  id: string;
  categoria: CategoriaEmpresa;
  capacidad: string; // clave única (lid_conciencia, gob_conciencia, …)
  nivel: NivelEmpresa;
  color: ColorProceso | null;
  observaciones: string | null;
  zenic?: ZenicEmpresa | null;
}

export interface HeatmapEmpresa {
  evaluacion: EvaluacionEmpresa;
  capacidades: CapacidadEvaluacionEmp[];
}

export interface SiembraEmpresa {
  capacidades: CapacidadEvaluacionEmp[];
  nivelGlobal: NivelEmpresa;
}

const BASE = '/process/madurez/empresa';

/** Lista las evaluaciones PEMM-Empresa del tenant (más reciente primero). */
export function fetchEvaluacionesEmpresa() {
  return request<{ data: EvaluacionEmpresa[] }>(`${BASE}/evaluaciones`);
}

/** Crea una evaluación PEMM-Empresa en BORRADOR para la organización. */
export function crearEvaluacionEmpresa(
  body: { tipoEjercicio?: TipoEjercicioEmpresa; observaciones?: string } = {},
) {
  return request<{ data: EvaluacionEmpresa }>(`${BASE}/evaluaciones`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Siembra las 13 capacidades en E1 (suelo del audit experto). */
export function sembrarCapacidadesEmpresa(evaluacionId: string) {
  return request<{ data: SiembraEmpresa }>(`${BASE}/evaluaciones/${evaluacionId}/sembrar`, {
    method: 'POST',
  });
}

/** Heatmap 13×4: cabecera + las 13 capacidades con su nivel. */
export function fetchHeatmapEmpresa(evaluacionId: string) {
  return request<{ data: HeatmapEmpresa }>(`${BASE}/evaluaciones/${evaluacionId}/heatmap`);
}

/** Ajuste del consultor sobre una capacidad (E1-E4). Recalcula el global (eslabón débil). */
export function actualizarCapacidad(
  evaluacionId: string,
  body: {
    capacidad: string;
    nivel: NivelEmpresa;
    observaciones?: string | null;
    zenic?: ZenicEmpresa | null;
  },
) {
  return request<{ data: CapacidadEvaluacionEmp }>(
    `${BASE}/evaluaciones/${evaluacionId}/capacidad`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
}

/**
 * Guarda UNA respuesta del cuestionario Empresa (0749). El backend rueda la media de
 * la capacidad a su nivel E y recomputa el global (MIN); devuelve el heatmap fresco.
 */
export function guardarRespuestaEmpresa(
  evaluacionId: string,
  body: { preguntaClave: string; valor: number; nota?: string | null },
) {
  return request<{ data: HeatmapEmpresa }>(`${BASE}/evaluaciones/${evaluacionId}/respuesta`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/** Conclusión del ingeniero (observaciones del header). */
export function guardarObservacionesEmpresa(evaluacionId: string, observaciones: string) {
  return request<{ data: EvaluacionEmpresa }>(
    `${BASE}/evaluaciones/${evaluacionId}/observaciones`,
    {
      method: 'PATCH',
      body: JSON.stringify({ observaciones }),
    },
  );
}

/** Aprueba la evaluación (inmutable). Requiere rol directivo (enforced en backend). */
export function aprobarEvaluacionEmpresa(evaluacionId: string) {
  return request<{ data: EvaluacionEmpresa }>(`${BASE}/evaluaciones/${evaluacionId}/aprobar`, {
    method: 'POST',
  });
}

// ── Análisis IA Empresa-nativo (espejo api-pemm-proceso · 0749) ───────────────
export type EstadoAnalisisEmpresa = 'BORRADOR' | 'GENERADO' | 'VALIDADO';

export interface RecomendacionEmpresa {
  categoria: CategoriaEmpresa;
  capacidad?: string;
  nivelGatillo?: NivelEmpresa | null;
  textoFinal: string;
  justificacion: string;
  prioridad: number;
}
export interface DafoEmpresa {
  fortalezas: string[];
  debilidades: string[];
  oportunidades: string[];
  amenazas: string[];
}
export interface SentinelFlagEmpresa {
  severity: string;
  message?: string;
}

/**
 * Roadmap y estudio TCO/ROI reutilizan los tipos de PEMM-Proceso (`RoadmapPemm` /
 * `TcoRoiPemm`): la metodología PICK + Kotter + DAFO cruzado y el modelo TCO/ROI son
 * agnósticos del instrumento (ninguno referencia `habilitador`). Reusar el tipo evita
 * duplicar 40+ líneas de interfaces idénticas y permite reutilizar `MadurezPemmRoadmap`
 * sin type-cast (estructuralmente compatible).
 */
export interface AnalisisEmpresa {
  id: string;
  diagnosticoNarrativo: string;
  recomendaciones: RecomendacionEmpresa[];
  fortalezas: string[];
  riesgos: string[];
  conclusionEjecutiva: string | null;
  matrizDafo: DafoEmpresa | null;
  roadmap?: RoadmapPemm | null;
  tcoRoi?: TcoRoiPemm | null;
  estado: EstadoAnalisisEmpresa;
  modeloUsado: string | null;
  proveedor: string | null;
  sentinelFlags?: SentinelFlagEmpresa[] | null;
  creadoEn: string;
}

/** Guarda el estudio de inversión TCO/ROI (editado por el ingeniero). */
export function guardarTcoRoiEmpresa(analisisId: string, tcoRoi: TcoRoiPemm) {
  return request<{ data: AnalisisEmpresa }>(`${BASE}/analisis/${analisisId}/tco-roi`, {
    method: 'PATCH',
    body: JSON.stringify({ tcoRoi }),
  });
}

/** Genera (o regenera) el análisis IA de una evaluación PEMM-Empresa. */
export function generarAnalisisEmpresa(evaluacionId: string) {
  return request<{ data: AnalisisEmpresa }>(`${BASE}/evaluaciones/${evaluacionId}/analisis`, {
    method: 'POST',
  });
}

/** Análisis IA vigente de una evaluación PEMM-Empresa (o null). */
export function fetchAnalisisVigenteEmpresa(evaluacionId: string) {
  return request<{ data: AnalisisEmpresa | null }>(`${BASE}/evaluaciones/${evaluacionId}/analisis`);
}

/** Histórico de análisis de una evaluación PEMM-Empresa (regeneraciones), recientes primero. */
export function fetchAnalisisHistoricoEmpresa(evaluacionId: string) {
  return request<{ data: AnalisisEmpresa[] }>(
    `${BASE}/evaluaciones/${evaluacionId}/analisis/historico`,
  );
}

/** Validación humana del análisis (GENERADO→VALIDADO). Requiere rol directivo. */
export function validarAnalisisEmpresa(analisisId: string) {
  return request<{ data: AnalisisEmpresa }>(`${BASE}/analisis/${analisisId}/validar`, {
    method: 'POST',
  });
}

// ── Exportación / informe (descargas binarias · espejo 0738 M3) ───────────────
// request() fuerza Content-Type JSON; las descargas usan fetch manual con Bearer.
async function descargarBlobEmpresa(path: string, fallbackName: string): Promise<void> {
  const token = getToken();
  const res = await fetch(`${API_ROOT}${path}`, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `Error ${res.status}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fallbackName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Descarga el megaprompt .md (snapshot Empresa embebido) para claude.ai. */
export function descargarMegapromptEmpresa(evaluacionId: string): Promise<void> {
  return descargarBlobEmpresa(
    `${BASE}/evaluaciones/${evaluacionId}/megaprompt`,
    'megaprompt-madurez-empresa.md',
  );
}

/** Obtiene el informe HTML autónomo y devuelve un object URL blob (el llamador lo revoca). */
export async function obtenerInformeEmpresaHtml(evaluacionId: string): Promise<string> {
  const token = getToken();
  const res = await fetch(`${API_ROOT}${BASE}/evaluaciones/${evaluacionId}/informe-html`, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? `Error ${res.status}`);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/** Descarga el informe Word (.docx) Empresa de un análisis VALIDADO+vigente. */
export function descargarInformeEmpresaWord(analisisId: string): Promise<void> {
  return descargarBlobEmpresa(
    `${BASE}/analisis/${analisisId}/informe`,
    'informe-madurez-empresa.docx',
  );
}

/** Descarga el exportador .py Empresa (genera el .docx desde claude.ai) de un análisis VALIDADO. */
export function descargarExportadorPyEmpresa(analisisId: string): Promise<void> {
  return descargarBlobEmpresa(
    `${BASE}/analisis/${analisisId}/exportador-py`,
    'generar_informe_madurez_empresa.py',
  );
}
